import type { ApiClient } from '@classgo/app-kit';
import type {
  CreateContractInput,
  CreateOwnerInput,
  CreateStaffUserInput,
  CreateTenantInput,
  UpdateContractInput,
  UpdateOwnerInput,
  UpdateTenantInput,
} from '@classgo/shared';
import { queryOptions } from '@tanstack/react-query';

import type {
  ActAsOpened,
  ContactRequestView,
  ContractView,
  StaffUserView,
  TenantDetail,
  TenantHealth,
  TenantUsers,
  TenantView,
} from './types';

/**
 * The `/staff/*` endpoints, typed by the shared input schemas.
 *
 * @param {ApiClient} api -- The client.
 * @returns One function per endpoint.
 */
export const staffApi = (api: ApiClient) => ({
  listTenants: () => api.request<TenantView[]>('/staff/tenants'),
  getTenant: (id: string) => api.request<TenantDetail>(`/staff/tenants/${id}`),
  createTenant: (body: CreateTenantInput) =>
    api.request<TenantView & { mainSedeId: string }>('/staff/tenants', { method: 'POST', body }),
  updateTenant: (id: string, body: UpdateTenantInput) =>
    api.request<TenantView>(`/staff/tenants/${id}`, { method: 'PATCH', body }),
  listContracts: (id: string) => api.request<ContractView[]>(`/staff/tenants/${id}/contracts`),
  createContract: (id: string, body: CreateContractInput) =>
    api.request<ContractView>(`/staff/tenants/${id}/contracts`, { method: 'POST', body }),
  updateContract: (id: string, contractId: string, body: UpdateContractInput) =>
    api.request<ContractView>(`/staff/tenants/${id}/contracts/${contractId}`, {
      method: 'PATCH',
      body,
    }),
  createOwner: (id: string, body: CreateOwnerInput) =>
    api.request<{ email?: string }>(`/staff/tenants/${id}/owners`, { method: 'POST', body }),
  updateOwner: (id: string, userId: string, body: UpdateOwnerInput) =>
    api.request<{ ok: boolean }>(`/staff/tenants/${id}/owners/${userId}`, {
      method: 'PATCH',
      body,
    }),
  removeOwner: (id: string, userId: string) =>
    api.request<{ ok: boolean }>(`/staff/tenants/${id}/owners/${userId}`, { method: 'DELETE' }),
  openActAs: (id: string, reason: string) =>
    api.request<ActAsOpened>(`/staff/tenants/${id}/act-as`, { method: 'POST', body: { reason } }),
  health: (id: string) => api.request<TenantHealth>(`/staff/tenants/${id}/health`),
  users: (id: string) => api.request<TenantUsers>(`/staff/tenants/${id}/users`),
  listStaffUsers: () => api.request<StaffUserView[]>('/staff/users'),
  listContactRequests: () => api.request<ContactRequestView[]>('/staff/contact-requests'),
  resetStaffMfa: (id: string) =>
    api.request<{ ok: boolean }>(`/staff/users/${id}/mfa-reset`, { method: 'POST' }),
  resetOwnerMfa: (tenantId: string, userId: string) =>
    api.request<{ ok: boolean }>(`/staff/tenants/${tenantId}/users/${userId}/mfa-reset`, {
      method: 'POST',
    }),
  createStaffUser: (body: CreateStaffUserInput) =>
    api.request<{ id: string; email: string; role: string }>('/staff/users', {
      method: 'POST',
      body,
    }),
});

/**
 * Query keys and options the screens and loaders share.
 */
export const staffQueries = {
  tenants: (api: ApiClient) =>
    queryOptions({ queryKey: ['staff', 'tenants'], queryFn: () => staffApi(api).listTenants() }),
  tenant: (api: ApiClient, id: string) =>
    queryOptions({
      queryKey: ['staff', 'tenants', id],
      queryFn: () => staffApi(api).getTenant(id),
    }),
  users: (api: ApiClient, id: string) =>
    queryOptions({
      queryKey: ['staff', 'tenants', id, 'users'],
      queryFn: () => staffApi(api).users(id),
    }),
  health: (api: ApiClient, id: string) =>
    queryOptions({
      queryKey: ['staff', 'tenants', id, 'health'],
      queryFn: () => staffApi(api).health(id),
    }),
  staffUsers: (api: ApiClient) =>
    queryOptions({ queryKey: ['staff', 'users'], queryFn: () => staffApi(api).listStaffUsers() }),
  contactRequests: (api: ApiClient) =>
    queryOptions({
      queryKey: ['staff', 'contact-requests'],
      queryFn: () => staffApi(api).listContactRequests(),
    }),
};
